import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const frameworks = [
  {
    id: 1,
    name: 'Category Reset',
    description: 'Redefine the competitive landscape by creating a new category or repositioning within an existing one.',
    impact: '3x inbound demand',
    icon: 'ri-compass-3-line',
  },
  {
    id: 2,
    name: 'Naming Multiplier',
    description: 'Strategic naming that clarifies positioning and accelerates market understanding.',
    impact: '2x conversion rate',
    icon: 'ri-text',
  },
  {
    id: 3,
    name: 'Positioning Clarity',
    description: 'Crystallize who you serve, what problem you solve, and why you win.',
    impact: '40% faster sales cycles',
    icon: 'ri-focus-3-line',
  },
  {
    id: 4,
    name: 'Pricing Architecture',
    description: 'Design pricing that captures value, segments customers, and drives expansion.',
    impact: '40% revenue increase',
    icon: 'ri-money-dollar-circle-line',
  },
  {
    id: 5,
    name: 'Distribution Engine',
    description: 'Build repeatable channels that compound over time instead of one-off campaigns.',
    impact: '5x pipeline growth',
    icon: 'ri-rocket-line',
  },
  {
    id: 6,
    name: 'Activation Accelerator',
    description: 'Remove friction from onboarding to get users to value faster.',
    impact: '2x activation rate',
    icon: 'ri-flashlight-line',
  },
  {
    id: 7,
    name: 'Narrative Engine',
    description: 'Craft a strategic story that positions your company in the market conversation.',
    impact: '3x media coverage',
    icon: 'ri-book-open-line',
  },
  {
    id: 8,
    name: 'Partnership Leverage',
    description: 'Structure partnerships that create asymmetric distribution advantages.',
    impact: '10x reach',
    icon: 'ri-links-line',
  },
  {
    id: 9,
    name: 'Decision Velocity',
    description: 'Remove organizational friction that slows execution and compounds delay.',
    impact: '3x shipping speed',
    icon: 'ri-speed-line',
  },
  {
    id: 10,
    name: 'Platform Transformation',
    description: 'Evolve from product to platform to unlock network effects and ecosystem value.',
    impact: '10x market cap',
    icon: 'ri-stack-line',
  },
];

const FrameworksGrid = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', (latest) => {
      setScrollProgress(latest);
    });
    return () => unsubscribe();
  }, [scrollYProgress]);

  // Title reveal
  const titleOpacity = useTransform(scrollYProgress, [0.1, 0.2], [0, 1]);
  const titleY = useTransform(scrollYProgress, [0.1, 0.2], [40, 0]);

  // Background glow intensity
  const glowOpacity = useTransform(scrollYProgress, [0.15, 0.4, 0.8], [0, 0.2, 0.1]);

  return (
    <section ref={containerRef} className="relative py-32 md:py-40 lg:py-48 px-6 md:px-8">
      {/* Section Background Glow */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: glowOpacity }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-blue rounded-full blur-[150px]"></div>
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto">
        <motion.div
          style={{
            opacity: titleOpacity,
            y: titleY
          }}
          className="text-center mb-16 md:mb-20 lg:mb-24"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 md:mb-8">
            The Ten Force Multipliers
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-400 max-w-[700px] mx-auto leading-relaxed">
            Each framework targets a specific structural constraint that limits output.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
          {frameworks.map((framework, index) => {
            // Progressive bottom-up reveal with stagger
            const cardProgress = Math.max(0, Math.min(1, (scrollProgress - 0.25 - index * 0.04) * 3));
            
            return (
              <motion.div
                key={framework.id}
                style={{
                  opacity: cardProgress,
                  y: (1 - cardProgress) * 60,
                  scale: 0.95 + cardProgress * 0.05
                }}
                className="group relative bg-dark-light/40 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-8 md:p-10 hover:border-primary/50 hover:bg-dark-light/60 transition-all duration-500 cursor-pointer min-h-[280px] md:min-h-[320px] flex flex-col"
              >
                {/* Icon */}
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-6 md:mb-8 group-hover:bg-primary/20 group-hover:border-primary/40 transition-all duration-500">
                  <i className={`${framework.icon} text-2xl md:text-3xl text-primary`}></i>
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3 md:mb-4 group-hover:text-primary transition-colors duration-300">
                    {framework.name}
                  </h3>
                  
                  <p className="text-sm md:text-base text-gray-400 leading-relaxed mb-6 flex-1">
                    {framework.description}
                  </p>

                  {/* Impact Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full self-start">
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                    <span className="text-xs md:text-sm font-semibold text-primary whitespace-nowrap">
                      {framework.impact}
                    </span>
                  </div>
                </div>

                {/* Hover Arrow */}
                <div className="absolute top-8 md:top-10 right-8 md:right-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <i className="ri-arrow-right-up-line text-xl md:text-2xl text-primary"></i>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FrameworksGrid;